import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import marketplaceService from '../services/marketplaceService';
import './VaultSourceList.css';

const MarketPlaceSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await marketplaceService.getAllSettings({});
      setSettings(response.settings || []);
    } catch (err) {
      console.error('Error loading marketplace settings:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load marketplace settings');
    } finally {
      setLoading(false);
    }
  };

  // Filter settings based on search term and active toggle
  const filteredSettings = settings.filter((setting) => {
    if (!showInactive && setting.SYSTEMACTIVE !== 'Y') return false;

    if (!searchTerm) return true;

    const search = searchTerm.toLowerCase();
    const id = (setting.MARKETPLACEID || '').toString().toLowerCase();
    const systemName = (setting.SYSTEMNAME || '').toLowerCase();
    const company = (setting.COMPANYNAME || '').toLowerCase();
    const role = (setting.SYSTEMROLE || '').toLowerCase();

    return id.includes(search) ||
           systemName.includes(search) ||
           company.includes(search) ||
           role.includes(search);
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this setting?')) {
      return;
    }

    try {
      await marketplaceService.deleteSetting(id);
      setSettings(settings.filter(s => s.id !== id));
    } catch (err) {
      console.error('Error deleting setting:', err);
      alert(err.response?.data?.message || 'Failed to delete setting');
    }
  };

  if (loading) {
    return <div className="loading">Loading marketplace settings...</div>;
  }

  return (
    <div className="vaultsource-list-container">
      <div className="header">
        <h1>Marketplace</h1>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/admin/marketplace/new')}
        >
          Add New Setting
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by ID, System Name, Company, or Role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="btn btn-secondary" onClick={() => setSearchTerm('')}>
          Clear
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
      </div>

      <div className="table-container">
        <table className="vaultsource-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>System Name</th>
              <th>Company</th>
              <th>Role</th>
              <th>Status</th>
              <th>Active</th>
              <th>Version</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSettings.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  {searchTerm ? 'No marketplace items match your search' : 'No marketplace items found'}
                </td>
              </tr>
            ) : (
              filteredSettings.map((setting) => (
                <tr
                  key={setting.MARKETPLACEID}
                  onClick={() => navigate(`/admin/marketplace/${setting.MARKETPLACEID}/edit`)}
                  className="clickable-row"
                  title="Click to edit"
                >
                  <td>{setting.MARKETPLACEID}</td>
                  <td>{setting.SYSTEMNAME}</td>
                  <td>{setting.COMPANYNAME}</td>
                  <td>{setting.SYSTEMROLE}</td>
                  <td>
                    <span className={`status-badge status-${(setting.SYSTEMSTATUS || '').toLowerCase()}`}>
                      {setting.SYSTEMSTATUS}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge status-${setting.SYSTEMACTIVE === 'Y' ? 'active' : 'inactive'}`}>
                      {setting.SYSTEMACTIVE === 'Y' ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>{setting.LATESTVERSION}</td>
                  <td className="actions">
                    <button
                      className="btn btn-edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/marketplace/${setting.MARKETPLACEID}/edit`);
                      }}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(setting.MARKETPLACEID);
                      }}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MarketPlaceSettings;
